/**
 * Local QA scenario contracts shared by the mode URL, NetworkManager, and the
 * memory-only server preparation handler.
 *
 * These are test harness coordinates/state, not game progression authority.
 * The server only honors them in MEMORY_ONLY_MODE for a GM session.
 */
export const HOLLOW_KEEPER_QA_SCENARIO = 'hollow_keeper' as const;

export const HOLLOW_KEEPER_QA_SETUP = Object.freeze({
  mapId: 'coil_undercroft',
  level: 12,
  questId: 'the_hollow_keeper',
  prerequisiteQuestId: 'whats_in_the_mist',
  objectiveTarget: 1,
  // Test-only staging point on the true y=1400 arena floor, beside the Keeper
  // in the clear west lane. A 40x40 player starts 40px beyond the Keeper's
  // 80px body and inside the Warrior's authored 150px ability range, so the
  // one-click route cannot masquerade a range rejection as a geometry failure.
  spawn: Object.freeze({ x: 360, y: 1360 }),
});

/**
 * `?qaScenario=movement` (2026-10-08): teach the player's class the movement
 * ability the Stone Golem finale grants, on any map, so each class's movement
 * can be tried without replaying the quest chain.
 */
export const MOVEMENT_KIT_QA_SCENARIO = 'movement' as const;
export const MOVEMENT_KIT_QUEST_ID = 'never_where_it_lands' as const;

/**
 * Plan 29 lane BS: teach the class every trainer ability at or under the
 * player's level through the trainer's grant (scripts/qa-ability-audit.mjs).
 */
export const TRAINER_KIT_QA_SCENARIO = 'trainer' as const;

/**
 * `?qaScenario=boss:<mobId>` (the next program, K8): stand beside that boss on the map the URL
 * enters (`map=<zone it spawns in>`), for its phases (`make boss-verify MOB=<mobId>`).
 */
export const BOSS_KIT_QA_PREFIX = 'boss:' as const;

export type QaScenarioPrepareCode =
  | 'MEMORY_MODE_REQUIRED'
  | 'GM_REQUIRED'
  | 'UNKNOWN_SCENARIO'
  | 'WRONG_MAP'
  | 'PLAYER_NOT_FOUND'
  | 'NO_MOVEMENT_ABILITY'
  | 'NOT_A_BOSS'
  | 'BOSS_NOT_HERE'
  | 'PREPARE_FAILED';

export type QaScenarioQuestStatus =
  | 'active'
  | 'ready_to_turn_in'
  | 'completed'
  | 'missing';

export interface QaScenarioPrepareResult {
  ok: boolean;
  scenario: string;
  code?: QaScenarioPrepareCode;
  mapId: string;
  level: number | null;
  questId: string;
  questStatus: QaScenarioQuestStatus;
  prerequisiteQuestId: string;
  prerequisiteCompleted: boolean;
  objectiveProgress: number;
  objectiveTarget: number;
  spawn?: { x: number; y: number };
  /** Movement scenario: the ability taught to this class. */
  abilityId?: string;
  /** Boss scenario: the boss's NPC id in the room, for `debug_hurtMob`. */
  npcId?: string;
  alreadyPrepared: boolean;
  /** QA setup is deliberately session-only and never invokes persistence. */
  persisted: false;
}
